import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { adsLaunchStatusLabelRu } from '@wit-support/ui-domain';
import { Checkbox, Label, TextLink, formatCount, toast } from '@wit-support/ui';
import { activateLaunch, getLaunch, reverifyLaunch } from '../api.js';
import { ADS_QUERY_DEFAULTS } from '../query-policy.js';
import { DiscrepancyTable } from './discrepancy-table.js';
import { formatStartInZone } from '../format.js';
import { displayName } from '../naming.js';
import { ADS_KEY, describeAdsError, isUpstreamCode, launchKey, useRegistry } from '../shared.js';
import { SpendConfirmDialog } from './spend-confirm-dialog.js';
import { spendButtonAmount, spendTotalText } from './spend-summary.js';
import { useAdsErrorToast } from './journal-link.js';
const DAY_FORMS = ['день', 'дня', 'дней'];
/** «9 ч назад», «35 мин назад», «3 дня назад» — по минутам сервера. */
export function agoText(minutes) {
    if (minutes < 1)
        return 'только что';
    if (minutes < 60)
        return `${minutes} мин назад`;
    if (minutes < 48 * 60)
        return `${Math.floor(minutes / 60)} ч назад`;
    return `${formatCount(Math.floor(minutes / (24 * 60)), DAY_FORMS)} назад`;
}
/** «больше суток» / «3 дня» — сколько запуск уже ждёт включения. */
export function waitingText(minutes) {
    const days = Math.floor(minutes / (24 * 60));
    return days >= 2 ? formatCount(days, DAY_FORMS) : 'больше суток';
}
/** Заголовок плашки опоздания по причине паузы (§11.5). */
export function lateTitle(reason) {
    if (reason === 'manual')
        return 'Запуск ждёт ручного включения, а время старта уже прошло';
    if (reason === 'start-too-close') {
        return 'Запуск создан на паузе (до старта было мало времени), время старта прошло';
    }
    return 'Запуск опоздал к старту — автоматически не включён';
}
/** «28.10 00:00 Лос-Анджелес (9 ч назад)» — старт по плану и давность от сервера. */
export function lateStartText(l) {
    const late = l.lateStart;
    const start = formatStartInZone({
        startTime: l.startTime ?? late?.startTime ?? null,
        startTimeLocal: l.startTimeLocal ?? null,
        startTimeZone: l.startTimeZone ?? null,
    }) ?? '—';
    return late ? `${start} (${agoText(late.lateByMinutes)})` : start;
}
/** «⚠ Запуск ждёт уже 3 дня — проверьте, что пост и настройки ещё актуальны». */
export function waitingOver24hText(minutes) {
    return `⚠ Запуск ждёт уже ${waitingText(minutes)} — проверьте, что пост и настройки ещё актуальны`;
}
/** Деньги запуска для диалога: строка сервера; нет — пусто (сумма неизвестна). */
export function launchSpendLines(l) {
    return l.spend ? [l.spend] : [];
}
/** «Понимаю: расход около $160.58 в день начнётся сразу»; суммы нет — без неё. */
export function acknowledgeText(lines) {
    // та же проверка «сумма уверенная», что у подписи кнопки
    if (spendButtonAmount(lines) == null)
        return 'Понимаю: расход начнётся сразу';
    return `Понимаю: расход около ${lines.map((x) => spendTotalText(x)).join(' + ')} в день начнётся сразу`;
}
/**
 * Тост итога включения — только то, что подтвердил статус из ответа сервера
 * (Codex): «включён» — при «Работает»/«Ждёт старта»; «Включается» — принято,
 * итог позже; иной статус — словами, без «включён».
 */
export function showActivationOutcome(r) {
    if (r.displayStatus === 'running') {
        toast.success('Запуск включён — показы и расход идут');
    }
    else if (r.displayStatus === 'waiting_start') {
        toast.success('Запуск включён — показы начнутся со времени старта');
    }
    else if (r.displayStatus === 'activating') {
        toast.info('Включение принято — статус обновится на карточке запуска');
    }
    else {
        toast.warning(`Запуск не включён — статус «${adsLaunchStatusLabelRu(r.displayStatus, r.activation?.reason).toLowerCase()}»`);
    }
}
/**
 * Блок «всё равно» (ревью SB-5 P1-1): первой строкой — что именно не так
 * (Meta сохранила не то, что мы отправили), список расхождений сверки и
 * отдельная галочка «Включить несмотря на расхождения сверки». После
 * перепроверки, снова нашедшей расхождения, — свежий список.
 */
function ForceDriftBlock({ launchId, rechecked, ack, onAck, }) {
    const registry = useRegistry();
    const detail = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: launchKey(launchId),
        queryFn: () => getLaunch(launchId),
    });
    const items = rechecked ?? detail.data?.verify?.discrepancies ?? [];
    const fields = new Map([...(registry.data?.fields ?? []), ...(registry.data?.options ?? [])].map((d) => [d.key, d]));
    const ackId = `force-ack-${launchId}`;
    return (_jsxs("div", { className: "flex flex-col gap-2", "data-testid": "late-activate-force", children: [_jsxs("p", { className: "font-medium text-[var(--color-state-danger-fg)]", children: [rechecked
                        ? '⛔ Перепроверка снова нашла расхождения — проверьте их и подтвердите ещё раз.'
                        : '⛔ Сверка нашла расхождения: Meta сохранила не то, что мы отправили.', ' ', "\u0412\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u0435 \u0437\u0430\u043F\u0443\u0441\u0442\u0438\u0442 \u0440\u0430\u0441\u0445\u043E\u0434 \u0441 \u044D\u0442\u0438\u043C\u0438 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0430\u043C\u0438.", ' ', _jsx(OpenLaunchLink, { id: launchId, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0442\u0430\u0431\u043B\u0438\u0446\u0443 \u0441\u0432\u0435\u0440\u043A\u0438" })] }), items.length > 0 ? (_jsx(DiscrepancyTable, { items: items, fields: fields })) : detail.isPending && !rechecked ? (_jsx("p", { className: "text-[var(--color-text-secondary)]", children: "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0440\u0430\u0441\u0445\u043E\u0436\u0434\u0435\u043D\u0438\u044F\u2026" })) : null, _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(Checkbox, { id: ackId, checked: ack, onCheckedChange: (v) => onAck(v === true) }), _jsx(Label, { htmlFor: ackId, className: "[overflow-wrap:anywhere]", children: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043D\u0435\u0441\u043C\u043E\u0442\u0440\u044F \u043D\u0430 \u0440\u0430\u0441\u0445\u043E\u0436\u0434\u0435\u043D\u0438\u044F \u0441\u0432\u0435\u0440\u043A\u0438" })] })] }));
}
/** Ссылка на карточку запуска («[Открыть]» в итоге прогона и в диалоге). */
export function OpenLaunchLink({ id, children = 'Открыть' }) {
    return (_jsx(TextLink, { asChild: true, children: _jsx(Link, { to: "/fb-launches/launches/$id", params: { id }, children: children }) }));
}
/**
 * «Включить сейчас?» (§11.5): итог в день крупно и в кнопке, галочка «Понимаю:
 * расход начнётся сразу», старт по плану и давность. Сверка старше 30 мин
 * (`verifyFresh = false`) — кнопка «Перепроверить и включить»: сначала
 * перепроверка; нашла расхождения — показать их в этом же диалоге и не
 * включать. После 24 ч ожидания — доп. предупреждение.
 */
export function LateActivateDialog({ open, onOpenChange, launch: l, force = false, forceReverify = false, onNotLate, }) {
    const qc = useQueryClient();
    const errorToast = useAdsErrorToast();
    const late = l.lateStart;
    const [stale, setStale] = useState(forceReverify || late?.verifyFresh === false);
    const [drift, setDrift] = useState(false);
    // «Включить сейчас всё равно» (force): отдельное согласие включить С РАСХОЖДЕНИЯМИ
    // (ревью SB-5 P1-1) и список расхождений, ради которых нужен force
    const [forceAck, setForceAck] = useState(false);
    // перепроверка в режиме force снова нашла расхождения — показать их и ждать
    // повторного подтверждения, не включать сразу
    const [rechecked, setRechecked] = useState(null);
    // состояние диалога — только на переходе «закрыт → открыт»: перечитывание
    // запуска после «Перепроверить» не стирает показанные расхождения (ревью SB-5)
    const wasOpen = useRef(false);
    useEffect(() => {
        if (open && !wasOpen.current) {
            setStale(forceReverify || late?.verifyFresh === false);
            setDrift(false);
            setForceAck(false);
            setRechecked(null);
        }
        wasOpen.current = open;
    }, [open, forceReverify, late?.verifyFresh]);
    const lines = launchSpendLines(l);
    const name = displayName(l.campaignName) ?? 'Запуск';
    // Цепочка «перепроверка → включение» (Codex P2): пока она идёт — закрыть нельзя;
    // закрытие/размонтирование аннулирует попытку — включение после «Отмена» не уходит.
    const [busy, setBusy] = useState(null);
    const attemptRef = useRef(0);
    useEffect(() => {
        if (!open)
            attemptRef.current += 1;
    }, [open]);
    useEffect(() => () => {
        attemptRef.current += 1;
    }, []);
    const confirm = async () => {
        const attempt = ++attemptRef.current;
        const live = () => attemptRef.current === attempt;
        try {
            if (stale) {
                setBusy('verify');
                const r = await reverifyLaunch(l.id);
                if (!live())
                    return;
                if (r.verify?.hasErrors === true && !force) {
                    setDrift(true);
                    void qc.invalidateQueries({ queryKey: ADS_KEY });
                    return;
                }
                if (r.verify?.hasErrors === true && force) {
                    // свежие расхождения — показать и снова спросить (галочка «несмотря на» снята);
                    // следующее нажатие включает по этой, уже свежей, сверке
                    setRechecked(r.verify.discrepancies ?? []);
                    setForceAck(false);
                    setStale(false);
                    void qc.invalidateQueries({ queryKey: ADS_KEY });
                    return;
                }
            }
            if (!live())
                return;
            setBusy('activate');
            const r = await activateLaunch(l.id, { acknowledgeLateStart: true, force });
            showActivationOutcome(r);
            onOpenChange(false);
        }
        catch (err) {
            // попытка аннулирована (диалог закрыт) — ничего не показываем и не продолжаем
            if (!live())
                return;
            if (isUpstreamCode(err, 'reverify-required')) {
                setStale(true);
                toast.warning('Сверка устарела — нажмите «Перепроверить и включить»');
            }
            else if (isUpstreamCode(err, 'verify-has-errors')) {
                setDrift(true);
            }
            else if (isUpstreamCode(err, 'late-start-not-applicable') && onNotLate) {
                toast.info(describeAdsError(err, 'write'));
                onNotLate();
            }
            else {
                errorToast(err);
            }
        }
        finally {
            if (live())
                setBusy(null);
            void qc.invalidateQueries({ queryKey: ADS_KEY });
        }
    };
    return (_jsx(SpendConfirmDialog, { open: open, onOpenChange: onOpenChange, title: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0441\u0435\u0439\u0447\u0430\u0441?", lines: lines, ...(l.spend == null ? { unknownLaunches: 1 } : {}), rows: [
            {
                label: 'Кампания',
                value: l.adAccountName ? `${name} · ${l.adAccountName}` : name,
            },
            { label: 'Старт по плану', value: lateStartText(l) },
        ], confirmLabel: stale ? 'Перепроверить и включить' : force ? 'Включить сейчас всё равно' : 'Включить', confirmVariant: force ? 'danger' : 'primary', acknowledgeLabel: acknowledgeText(lines), blockedReason: drift
            ? 'Сверка нашла расхождения — включать нельзя'
            : force && !forceAck
                ? 'Отметьте «Включить несмотря на расхождения сверки»'
                : null, onConfirm: confirm, busy: busy != null, busyText: busy === 'verify' ? 'Проверяем запуск в Meta…' : 'Включаем…', children: _jsxs("div", { className: "flex flex-col gap-2", "data-testid": "late-activate-text", children: [force ? (_jsx(ForceDriftBlock, { launchId: l.id, rechecked: rechecked, ack: forceAck, onAck: setForceAck })) : null, _jsx("p", { children: "\u041F\u043E\u0441\u043B\u0435 \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043F\u043E\u043A\u0430\u0437\u044B \u0438 \u0440\u0430\u0441\u0445\u043E\u0434 \u043D\u0430\u0447\u043D\u0443\u0442\u0441\u044F \u0441\u0440\u0430\u0437\u0443." }), _jsx("p", { className: "text-[var(--color-text-secondary)]", children: "\u24D8 \u0414\u0435\u043D\u044C \u043F\u043E \u0432\u0440\u0435\u043C\u0435\u043D\u0438 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 \u0443\u0436\u0435 \u0438\u0434\u0451\u0442: Meta \u043C\u043E\u0436\u0435\u0442 \u043F\u043E\u0442\u0440\u0430\u0442\u0438\u0442\u044C \u0434\u043D\u0435\u0432\u043D\u043E\u0439 \u0431\u044E\u0434\u0436\u0435\u0442 \u0437\u0430 \u043E\u0441\u0442\u0430\u0432\u0448\u0438\u0435\u0441\u044F \u0447\u0430\u0441\u044B; \u0432 \u043E\u0442\u0434\u0435\u043B\u044C\u043D\u044B\u0435 \u0434\u043D\u0438 Meta \u0442\u0440\u0430\u0442\u0438\u0442 \u0431\u043E\u043B\u044C\u0448\u0435 \u0431\u044E\u0434\u0436\u0435\u0442\u0430, \u0432 \u0441\u0440\u0435\u0434\u043D\u0435\u043C \u0437\u0430 \u043D\u0435\u0434\u0435\u043B\u044E \u2014 \u043D\u0435 \u0431\u043E\u043B\u044C\u0448\u0435." }), late?.waitingOver24h ? (_jsx("p", { className: "font-medium text-[var(--color-state-warning-fg)]", children: waitingOver24hText(late.lateByMinutes) })) : null, stale && !drift ? (_jsxs("p", { "data-testid": "late-activate-stale", children: ["\u0421\u0432\u0435\u0440\u043A\u0430 \u0441 Meta \u0431\u044B\u043B\u0430", ' ', late?.verifiedAgoMinutes != null ? agoText(late.verifiedAgoMinutes) : 'давно', " \u2014 \u043F\u0435\u0440\u0435\u0434 \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u0435\u043C \u043F\u0435\u0440\u0435\u043F\u0440\u043E\u0432\u0435\u0440\u0438\u043C, \u0447\u0442\u043E \u0432 Meta \u0432\u0441\u0451 \u043A\u0430\u043A \u0437\u0430\u0434\u0443\u043C\u0430\u043D\u043E."] })) : null, drift ? (_jsxs("p", { className: "font-medium text-[var(--color-state-danger-fg)]", "data-testid": "late-activate-drift", children: ["\u26D4 \u041F\u0435\u0440\u0435\u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0430 \u043D\u0430\u0448\u043B\u0430 \u0440\u0430\u0441\u0445\u043E\u0436\u0434\u0435\u043D\u0438\u044F: Meta \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u043B\u0430 \u043D\u0435 \u0442\u043E, \u0447\u0442\u043E \u043C\u044B \u043E\u0442\u043F\u0440\u0430\u0432\u0438\u043B\u0438. \u0417\u0430\u043F\u0443\u0441\u043A \u043D\u0435 \u0432\u043A\u043B\u044E\u0447\u0451\u043D. ", _jsx(OpenLaunchLink, { id: l.id, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0442\u0430\u0431\u043B\u0438\u0446\u0443 \u0441\u0432\u0435\u0440\u043A\u0438" })] })) : null] }) }));
}
//# sourceMappingURL=late-activation.js.map